import type { FC, MouseEvent } from "react";

import { copyToClipboard } from "@ui/components/CopyButton";
import { BACKGROUND_OPTIONS, Dialog } from "@ui/components/Dialog";
import { RedirectionLinkCard } from "@ui/components/RedirectionLinkCard";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useKakao } from "@ui/hooks/useKakao/useKakao";
import { useTranslations } from "@ui/providers/useTranslations";
import { type MessengerConfig, providerHref, type ShareProvider } from "@ui/utils/share";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import type { DirectionalIconComponent } from "@jti/brand-icons";
import { ChatIcon } from "@jti/brand-icons/chat";
import { SocialFacebookMessengerIcon } from "@jti/brand-icons/social-facebook-messenger";
import { SocialKakaoIcon } from "@jti/brand-icons/social-kakao";
import { SocialMailIcon } from "@jti/brand-icons/social-mail";
import { SocialTelegramIcon } from "@jti/brand-icons/social-telegram";
import { SocialViberIcon } from "@jti/brand-icons/social-viber";
import { SocialWhatsappIcon } from "@jti/brand-icons/social-whatsapp";
import { UiCopyOutlineIcon } from "@jti/brand-icons/ui-copy-outline";
import { toast } from "@jti/core-components/components/Toaster";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import styles from "./ReferralPersonalCodeShareDialog.module.scss";

type GenericShareProvider = Exclude<ShareProvider, "copy" | "kakao">;

export type ShareDialogProps = {
  open: boolean;
  title?: string;
  description?: string;
  onClose: () => void;
  providers: ShareProvider[];
  shareUrl: string;
  shareTextPrefix?: string;
  messenger?: MessengerConfig;
};

type DeterminedProviderMeta = { label: string; Icon: DirectionalIconComponent };
type UndeterminedProviderMeta = DeterminedProviderMeta | { labelKey: string; Icon: DirectionalIconComponent };

const providerMeta: Record<ShareProvider, UndeterminedProviderMeta> = {
  whatsapp: { label: "WhatsApp", Icon: SocialWhatsappIcon },
  telegram: { label: "Telegram", Icon: SocialTelegramIcon },
  messenger: { label: "Messenger", Icon: SocialFacebookMessengerIcon },
  viber: { label: "Viber", Icon: SocialViberIcon },
  email: { label: "Email", Icon: SocialMailIcon },
  sms: { label: "SMS", Icon: ChatIcon },
  copy: { labelKey: "loyalty.referral_personal_code.copy_link", Icon: UiCopyOutlineIcon },
  kakao: { label: "Kakao", Icon: SocialKakaoIcon },
};

export const ReferralPersonalCodeShareDialog: FC<ShareDialogProps> = ({
  open,
  title,
  description,
  onClose,
  providers,
  shareUrl,
  shareTextPrefix,
  messenger,
}) => {
  const { breakpoint } = useDeviceBreakpoint();
  const { __ } = useTranslations();
  const createMetaWithLabel: (meta: UndeterminedProviderMeta) => DeterminedProviderMeta = (meta) => {
    if ("label" in meta) {
      return meta;
    }

    const { labelKey, ...rest } = meta;

    return {
      ...rest,
      label: __(labelKey),
    };
  };

  const filteredProvidersMeta = providers.reduce<{ key: ShareProvider; Meta: DeterminedProviderMeta }[]>((acc, key) => {
    const Meta = providerMeta[key];
    if (Meta) {
      acc.push({ key, Meta: createMetaWithLabel(Meta) });
    }

    return acc;
  }, []);
  const kakaoMeta = filteredProvidersMeta.find(({ key }) => key === "kakao");
  const { kakaoSdkLink, kakaoSdkKey, kakaoIntegrityKey } = useConfig();
  useKakao({
    kakaoSdkLink,
    integrityKey: kakaoIntegrityKey,
    sdkKey: kakaoSdkKey,
    isEnabled: Boolean(kakaoMeta),
  });

  const handleCopyProvider = () => {
    copyToClipboard(shareUrl);
    toast.success(__("copy.code.button"));
    dispatchTrackingEvent(TrackingEvents.TrackingSocialIconClick, { social_network: "copy" });
  };

  const handleKakaoProvider = () => {
    dispatchTrackingEvent(TrackingEvents.TrackingSocialIconClick, { social_network: "kakao" });

    window.Kakao?.Share?.sendDefault({
      objectType: "text",
      text: `${shareTextPrefix} ${shareUrl}`.trim(),
      link: {
        webUrl: shareUrl,
        mobileWebUrl: shareUrl,
      },
    });
  };

  const handleGenericProvider = (provider: GenericShareProvider) => {
    const href = providerHref(provider, shareUrl, provider === "messenger" ? undefined : shareTextPrefix, messenger);
    if (href) {
      dispatchTrackingEvent(TrackingEvents.TrackingSocialIconClick, { social_network: provider });
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const handleProviderClick = (provider: ShareProvider, e: MouseEvent) => {
    e.preventDefault();

    switch (provider) {
      case "copy":
        handleCopyProvider();
        break;
      case "kakao":
        handleKakaoProvider();
        break;
      default:
        handleGenericProvider(provider);
        break;
    }

    onClose();
  };

  return (
    <Dialog
      isOpened={open}
      onClose={onClose}
      backgroundColor={BACKGROUND_OPTIONS.blurredWhite.color}
      backgroundBlur={BACKGROUND_OPTIONS.blurredWhite.blur}
      dataTestId="share-dialog"
      ariaLabelledBy="share-dialog-title"
      fullscreen={breakpoint === "mobile"}
    >
      <div className={styles.shareDialog}>
        <div id="share-dialog-header" className={styles.shareDialogHeader}>
          <div id="share-dialog-title" className={styles.shareDialogTitle}>
            {title}
          </div>
          <div id="share-dialog-description" className={styles.shareDialogDescription}>
            {description}
          </div>
        </div>
        <div
          className={classNames(styles.shareDialogList, {
            [styles.shareDialogListSingleElement]: filteredProvidersMeta.length === 1,
          })}
          data-testid="share-dialog-providers"
        >
          {filteredProvidersMeta.map(({ key, Meta }) => {
            return (
              <RedirectionLinkCard
                variant="button"
                title={Meta.label}
                key={key}
                onClick={(e) => handleProviderClick(key, e)}
                dataTestid={`share-provider-${key}`}
                iconElement={Meta.Icon}
                className={styles.shareDialogItem}
              />
            );
          })}
        </div>
      </div>
    </Dialog>
  );
};
